import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

type TournamentPhase = 'groups' | 'elimination' | 'final' | 'finished';

interface TournamentProgress {
  phase: TournamentPhase;
  progress: number;
  loading: boolean;
}

export function useTournamentProgress(eventId: string) {
  const { user } = useAuth();
  const [progress, setProgress] = useState<TournamentProgress>({
    phase: 'groups',
    progress: 33,
    loading: true
  });

  useEffect(() => {
    async function fetchProgress() {
      if (!user) return;

      try {
        // Get event status
        const { data: event } = await supabase
          .from('events')
          .select('status, tournament_format')
          .eq('id', eventId)
          .single();

        if (!event) {
          setProgress({ phase: 'groups', progress: 33, loading: false });
          return;
        }

        // Get user's registration to find their name
        const { data: registration } = await supabase
          .from('registrations')
          .select('player1_name, player2_name, user_id, partner_user_id')
          .eq('event_id', eventId)
          .or(`user_id.eq.${user.id},partner_user_id.eq.${user.id}`)
          .eq('status', 'confirmed')
          .maybeSingle();

        if (!registration) {
          setProgress({ phase: 'groups', progress: 33, loading: false });
          return;
        }

        const userName = registration.user_id === user.id 
          ? registration.player1_name 
          : registration.player2_name;

        // Check if user has elimination matches
        const { data: eliminationMatches } = await supabase
          .from('tournament_match_results')
          .select('phase')
          .eq('tournament_id', eventId)
          .or(`team1_player1.eq.${userName},team2_player1.eq.${userName}`)
          .order('completed_at', { ascending: false });

        if (event.status === 'completed') {
          setProgress({ phase: 'finished', progress: 100, loading: false });
          return;
        }

        if (eliminationMatches && eliminationMatches.length > 0) {
          const latestPhase = eliminationMatches[0].phase;
          
          if (latestPhase === 'final') {
            setProgress({ phase: 'final', progress: 100, loading: false });
          } else if (latestPhase === 'semifinal') {
            setProgress({ phase: 'final', progress: 100, loading: false });
          } else {
            setProgress({ phase: 'elimination', progress: 66, loading: false });
          }
        } else {
          setProgress({ phase: 'groups', progress: 33, loading: false });
        }
      } catch (error) {
        console.error('[useTournamentProgress] Error:', error);
        setProgress({ phase: 'groups', progress: 33, loading: false });
      }
    }

    fetchProgress();
  }, [eventId, user]);

  return progress;
}
